/**
 * Generic mock data generator that creates realistic data for any GraphQL type
 * Handles scalars, objects, arrays, and optional fields
 */

export type ScalarGenerators = {
  [scalarName: string]: () => any
}

export type MockDataOptions = {
  /** Custom generators for scalar types */
  scalars?: ScalarGenerators
  /** Override specific field values */
  overrides?: Record<string, any>
  /** Recursion depth limit for nested objects */
  maxDepth?: number
}

const defaultScalarGenerators: ScalarGenerators = {
  String: () => 'mock-string',
  Int: () => Math.floor(Math.random() * 100),
  Float: () => Math.random() * 100,
  Boolean: () => Math.random() > 0.5,
  ID: () => `id-${Math.random().toString(36).substr(2, 9)}`,
  UUID: () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  },
  JSON: () => ({}),
  Time: () => new Date().toISOString(),
  Upload: () => null,
}

/**
 * Creates mock data for a given GraphQL type
 * @param typeDefinition - The GraphQL type definition object with __typename and field definitions
 * @param options - Configuration for data generation
 * @returns Generated mock data object
 *
 * @example
 * const mockTag = createMockData(Tag, {
 *   overrides: { id: 'custom-id' }
 * })
 *
 * const mockTags = Array.from({ length: 5 }, () =>
 *   createMockData(Tag)
 * )
 */
export function createMockData(
  typeDefinition: Record<string, any>,
  options: MockDataOptions = {},
): any {
  const { scalars = {}, overrides = {}, maxDepth = 3 } = options

  const mergedScalars = { ...defaultScalarGenerators, ...scalars }

  function generate(type: any, depth = 0): any {
    if (depth > maxDepth) {
      return null
    }

    // Handle primitives
    if (typeof type !== 'object' || type === null) {
      return type
    }

    // Handle arrays
    if (Array.isArray(type)) {
      return [generate(type[0], depth + 1)]
    }

    // Handle object types
    const result: Record<string, any> = {}

    for (const [key, value] of Object.entries(type)) {
      // Skip internal fields and typename
      if (key.startsWith('__') || key === '_typename') {
        continue
      }

      // Check for overrides first
      if (key in overrides) {
        result[key] = overrides[key]
        continue
      }

      // Handle nullable/optional fields
      if (value === null || value === undefined) {
        result[key] = null
        continue
      }

      // Handle scalar types
      if (typeof value === 'string') {
        const generator = mergedScalars[value]
        result[key] = generator
          ? generator()
          : generateFromTypeName(value, mergedScalars)

        continue
      }

      // Handle nested objects
      if (typeof value === 'object') {
        result[key] = generate(value, depth + 1)

        continue
      }

      // Handle boolean values (for maybe types)
      if (typeof value === 'boolean') {
        result[key] = value

        continue
      }
    }

    return result
  }

  return generate(typeDefinition)
}

/**
 * Generate data based on a scalar type name (fallback for unknown scalars)
 */
function generateFromTypeName(
  typeName: string,
  generators: ScalarGenerators,
): any {
  const lowerName = typeName.toLowerCase()

  if (lowerName.includes('id')) {
    return generators.ID?.() ?? `id-${Math.random().toString(36).substr(2, 9)}`
  }
  if (lowerName.includes('uuid')) {
    return generators.UUID?.() ?? generateUUID()
  }
  if (lowerName.includes('time') || lowerName.includes('date')) {
    return generators.Time?.() ?? new Date().toISOString()
  }
  if (lowerName.includes('boolean') || lowerName.includes('bool')) {
    return generators.Boolean?.() ?? false
  }
  if (lowerName.includes('int') || lowerName.includes('number')) {
    return generators.Int?.() ?? 0
  }
  if (lowerName.includes('float') || lowerName.includes('decimal')) {
    return generators.Float?.() ?? 0
  }

  // Default to string
  return generators.String?.() ?? 'mock-value'
}

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8

    return v.toString(16)
  })
}
